/**
 * attachmentRecords.ts — ONE way to turn a wire attachment into the SerializedAttachment the chat
 * renders, and the media rules every seam must agree on.
 *
 * WHY (2026-09-08, Miles: "when a user plays back a video it needs to be smooth, every time"):
 * three hand-written mappers (the SSE finalize in useGlobalTools, the deep-research deliverable
 * builder, the task-recovery path in usePrest0nSession) each kept `storageUrl` and DROPPED
 * `storagePath` / `gcsPath` — so a freshly finalized message had no way to re-mint a URL once the
 * 7-day signed link expired — and the history sync deliberately re-attached a cached base64 dataUrl
 * onto records that already had a storage URL. For a 3 MB MP4 that is a 4 MB string in React state
 * and localStorage, and a `<video src="data:…">` that cannot seek, cannot stream and is never
 * media-cached: the choppy playback the owner reported, every time.
 *
 * Rules, in one place:
 *   - every durable locator survives (storageUrl, storagePath, gcsPath, generatedBy);
 *   - a `dataUrl` is carried ONLY when the record has no durable locator (a base64 body is a
 *     fallback for the un-uploaded, never a companion to a stored object);
 *   - a stored VIDEO or AUDIO record never regains a local base64 copy from the history cache.
 */
import type { SerializedAttachment } from './types';

/** The loose shape attachments arrive in on the wire (SSE done, task records, deliverables). */
export interface RawAttachmentRecord {
  name?: unknown;
  type?: unknown;
  size?: unknown;
  storageUrl?: unknown;
  storagePath?: unknown;
  gcsPath?: unknown;
  generatedBy?: unknown;
  /** Inline base64 body (no data: prefix) — the tool-result shape before upload. */
  data?: unknown;
  dataUrl?: unknown;
  docId?: unknown;
  docVersion?: unknown;
  /** ms since epoch when `storageUrl` was minted (PR-6, 2026-09-09). */
  storageUrlMintedAt?: unknown;
  lastModified?: unknown;
  /** The picture's own pixel size and its sub-kilobyte placeholder (2026-09-11). */
  width?: unknown;
  height?: unknown;
  preview?: unknown;
  /** A displayable JPEG copy for an original this browser may not decode
   *  (2026-09-12, HEIC). */
  displayUrl?: unknown;
  displayPath?: unknown;
}

/**
 * A signed download URL is minted for 7 days. Past THIS age the image tile re-mints
 * (owner via `storagePath`, everyone via the resolver) before the URL can break —
 * a picture never goes blank on day 8 (chat-thread PR-6, 2026-09-09).
 */
export const SIGNED_URL_STALE_MS = 6 * 24 * 60 * 60 * 1000;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

/** True for a record the chat plays rather than shows: video/* or audio/*. */
export function isMediaAttachmentType(type: string | null | undefined): boolean {
  const t = String(type || '').toLowerCase();
  return t.startsWith('video/') || t.startsWith('audio/');
}

/** True when the record can be fetched AGAIN from storage — any durable locator. */
export function hasDurableAttachmentLocator(att: { storageUrl?: unknown; storagePath?: unknown; gcsPath?: unknown } | null | undefined): boolean {
  return !!(att && (str(att.storageUrl) || str(att.storagePath) || str(att.gcsPath)));
}

/**
 * The chat's SerializedAttachment for a wire record, or null when the record has no name.
 * Durable locators first; a base64 body becomes a dataUrl only when nothing durable exists.
 */
export function toSerializedAttachment(raw: RawAttachmentRecord | null | undefined): SerializedAttachment | null {
  if (!raw || typeof raw !== 'object') return null;
  const name = str(raw.name);
  if (!name) return null;
  const type = str(raw.type) || '';
  const size = typeof raw.size === 'number' && Number.isFinite(raw.size) && raw.size >= 0 ? raw.size : 0;
  const out: SerializedAttachment = { name, type, size };
  const storageUrl = str(raw.storageUrl);
  const storagePath = str(raw.storagePath);
  const gcsPath = str(raw.gcsPath);
  const generatedBy = str(raw.generatedBy);
  if (storageUrl) out.storageUrl = storageUrl;
  if (storagePath) out.storagePath = storagePath;
  if (gcsPath) out.gcsPath = gcsPath;
  if (generatedBy) out.generatedBy = generatedBy;
  if (typeof raw.docId === 'string' && raw.docId) out.docId = raw.docId;
  if (typeof raw.docVersion === 'number') out.docVersion = raw.docVersion;
  if (storageUrl && typeof raw.storageUrlMintedAt === 'number' && Number.isFinite(raw.storageUrlMintedAt) && raw.storageUrlMintedAt > 0) {
    out.storageUrlMintedAt = raw.storageUrlMintedAt;
  }
  // Dimensions + placeholder survive a reload and a merge (2026-09-11). Both
  // hydrate paths come through here, which is why the earlier field list that
  // was hand-written per path kept dropping `gcsPath` — one mapper, or the
  // next field added is the next one silently lost.
  if (typeof raw.width === 'number' && Number.isFinite(raw.width) && raw.width > 0) out.width = Math.floor(raw.width);
  if (typeof raw.height === 'number' && Number.isFinite(raw.height) && raw.height > 0) out.height = Math.floor(raw.height);
  if (typeof raw.preview === 'string' && /^data:image\//.test(raw.preview)) out.preview = raw.preview;
  // The display rendition (2026-09-12), and the comment above is why this line
  // exists rather than being discovered missing later: this reviver is an
  // ALLOWLIST, so a field the wire carries and this does not copy simply never
  // reaches the render. That is precisely how `storageUrlMintedAt` was dead for
  // every recipient for two days. https only — it is set as an <img> src.
  if (typeof raw.displayUrl === 'string' && /^https:\/\//i.test(raw.displayUrl)) out.displayUrl = raw.displayUrl;
  if (typeof raw.displayPath === 'string' && raw.displayPath.trim()) out.displayPath = raw.displayPath.trim();
  if (!hasDurableAttachmentLocator(out)) {
    const dataUrl = str(raw.dataUrl) || (str(raw.data) && type ? `data:${type};base64,${raw.data}` : undefined);
    if (dataUrl) out.dataUrl = dataUrl;
  }
  return out;
}

/**
 * The history sync's question: may this record regain the base64 copy localStorage still holds?
 * Yes for documents and pictures (instant local download, cross-device access via the URL); NEVER
 * for a stored video or audio file — the durable URL is the only src that streams and seeks.
 */
export function shouldRestoreLocalDataUrl(att: { type?: unknown; dataUrl?: unknown; storageUrl?: unknown; storagePath?: unknown; gcsPath?: unknown } | null | undefined): boolean {
  if (!att || str(att.dataUrl)) return false;
  if (isMediaAttachmentType(str(att.type)) && hasDurableAttachmentLocator(att)) return false;
  return true;
}

/** True when the record's signed URL is older than SIGNED_URL_STALE_MS — re-mint before it breaks. */
export function isStaleSignedUrl(
  att: { storageUrl?: unknown; storageUrlMintedAt?: unknown } | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!att || !str(att.storageUrl)) return false;
  const minted = att.storageUrlMintedAt;
  return typeof minted === 'number' && Number.isFinite(minted) && minted > 0 && now - minted > SIGNED_URL_STALE_MS;
}

/**
 * The hydrate mapper — localStorage and the Firestore mirror back into the message list
 * (PR-6, 2026-09-09). Before this, the two hand-written mappers in useGlobalChat kept
 * name/type/size/dataUrl/storageUrl/storagePath and DROPPED everything else — a
 * cloud-ingest upload (gcsPath only) lost its one locator on the next reload and the
 * tile fell to the placeholder. Rules: every durable locator survives
 * (toSerializedAttachment), `lastModified` and the URL's mint stamp ride along, and the
 * local base64 copy is kept only where shouldRestoreLocalDataUrl allows it (documents and
 * pictures: yes; a stored video or audio file: never). A nameless record is kept under
 * 'attachment' so a count never silently drops.
 */
export function reviveSerializedAttachment(raw: unknown): SerializedAttachment | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as RawAttachmentRecord;
  const out = toSerializedAttachment({ ...rec, name: str(rec.name) || 'attachment' });
  if (!out) return null;
  if (typeof rec.lastModified === 'number' && Number.isFinite(rec.lastModified)) out.lastModified = rec.lastModified;
  const dataUrl = str(rec.dataUrl);
  if (dataUrl && !out.dataUrl && shouldRestoreLocalDataUrl({
    type: out.type, storageUrl: out.storageUrl, storagePath: out.storagePath, gcsPath: out.gcsPath,
  })) {
    out.dataUrl = dataUrl;
  }
  return out;
}
